import type { ReactNode } from 'react';
import { createContext } from 'react';
import type { MakeGivenKeysOptional } from '@common/utils/utilityTypes';
import type { ValidationErrors } from '../../utils/validation';
import { translateKey } from '../../translate';

type MergedKeys =
    | 'value'
    | 'onChange'
    | 'onBlur'
    | 'placeholder'
    | 'onDoValidate'
    | 'error'
    | 'checked'
    | 'name'
    | 'hasErrors'
    | 'label';
/*
    The FormContextProps alters a given type. The resulting type is a union of the original type and the original type with added 'fieldKey' property and all the columns provided by the formContext made optional.
    So for example:
    interface IInputComponentProps {
        value: string;
        onChange: (value: string) => void;
        something: number;
    }
    then:
    FormContextProps<IInputComponentProps> = {
        fieldKey: string;
        value?: string;
        onChange?: (value: string) => void;
        something: number;
    } | {
        value: string;
        onChange: (value: string) => void;
        something: number;
    }
    It should be used by input components that render the FormContextConsumer component to type its props.
*/
export type FormContextProps<T> = T | MakeGivenKeysOptional<T & { fieldKey: string }, MergedKeys & keyof T>;

// FormContextProviders should provide this type as value
export interface IFormContext<ItemType> {
    customControlData?: boolean;
    errors: ValidationErrors;
    item: ItemType;
    onBlur?: (key: keyof ItemType, value: ItemType[keyof ItemType]) => void;
    onChange: <Key extends keyof ItemType>(key: Key, value: ItemType[Key]) => void;
    onDoValidate: (key: keyof ItemType) => void;
    placeholderGroupName: string;
}

interface IFormContextConsumerProps<IElementProps> {
    children: (props: IElementProps & Pick<Required<IElementProps>, keyof IElementProps & MergedKeys>) => ReactNode;
}

const defaultFormContext: IFormContext<{}> = undefined as any;

export const { Provider: FormContextProvider, Consumer: InternalFormContextConsumer } =
    createContext(defaultFormContext);

export function FormContextConsumer<IElementProps>(props: IElementProps & IFormContextConsumerProps<IElementProps>) {
    return (
        <InternalFormContextConsumer>
            {(context) => {
                if ((props as any).fieldKey !== undefined && context === undefined) {
                    throw new Error(
                        `Element has fieldKey "${(props as any).fieldKey}" but no FormContext was provided.`
                    );
                }
                const properties = mergePropsWithFormContext(props, context);
                // cast as any because generics cannot be spread. See https://github.com/Microsoft/TypeScript/issues/16780
                const { children, ...childProps } = properties as any;

                return <>{children(childProps)}</>;
            }}
        </InternalFormContextConsumer>
    );
}

function mergePropsWithFormContext(props: any, provider: IFormContext<any>) {
    const mergedProps = { ...props };
    if (props.fieldKey === undefined) {
        return mergedProps;
    }

    const fieldKey = props.fieldKey;
    if (props.value === undefined) {
        mergedProps.value = provider.item[fieldKey];
    }
    if (props.onChange === undefined) {
        mergedProps.onChange = (value: any) => provider.onChange(fieldKey, value);
    }
    if (props.onBlur === undefined) {
        mergedProps.onBlur = (value: any) => provider.onBlur?.(fieldKey, value);
    }
    if (props.placeholder === undefined) {
        mergedProps.placeholder = translateKey(provider.placeholderGroupName + '.' + fieldKey);
    }
    if (props.onDoValidate === undefined) {
        mergedProps.onDoValidate = () => provider.onDoValidate(fieldKey);
    }
    if (props.error === undefined) {
        if (provider.customControlData) {
            mergedProps.error = provider.errors.get(`customControlData.${fieldKey}`);
        } else {
            mergedProps.error = provider.errors.get(fieldKey);
        }
    }
    if (props.checked === undefined) {
        mergedProps.checked = !!provider.item[fieldKey];
    }
    if (props.name === undefined) {
        mergedProps.name = fieldKey;
    }
    if (props.hasErrors === undefined) {
        if (provider.customControlData) {
            mergedProps.hasErrors = provider.errors.has(`customControlData.${fieldKey}`);
        } else {
            mergedProps.hasErrors = provider.errors.has(fieldKey);
        }
    }
    if (props.label === undefined) {
        mergedProps.label = mergedProps.placeholder;
    }

    return mergedProps;
}
